'use client';

import { type ComponentPropsWithoutRef, useMemo } from 'react';
import styled from 'styled-components';
import type {
  AvatarShapes,
  AvatarSizes,
  ThemeColorsBackground,
  ThemeColorsMeta,
} from '@frameio/vapor';
import { Box, squareRadius, theme, usePreviousDistinct } from '@frameio/vapor';
import { useColorThief } from '../../../hooks/useColorThief';

type AvatarHaloProps = ComponentPropsWithoutRef<typeof Box> & {
  alt: string;
  fallback:
    | `transparent`
    | `var(--colors-${string})`
    | ThemeColorsBackground
    | ThemeColorsMeta;
  isTransitioning?: boolean;
  shape: AvatarShapes;
  size: AvatarSizes;
  src?: string;
  transitionDurationSeconds?: number;
  transitionDelaySeconds?: number;
};

const HaloBackground = styled(Box)<{
  $backgroundColor: string;
  $shape: string;
  $size: AvatarSizes;
  $transitionDelaySeconds?: number;
  $transitionDurationSeconds?: number;
}>`
  background-color: ${(p) => p.$backgroundColor || 'transparent'};
  border-radius: ${(p) =>
    p.$shape === 'circle' ? '50%' : `${squareRadius(p.$size)}px`};
  bottom: 0;
  filter: blur(40px);
  height: ${(p) => p.$size}px;
  isolation: isolate;
  opacity: 0.4;
  pointer-events: none;
  position: absolute;
  transition-delay: ${(p) => p.$transitionDelaySeconds ?? 0.2}s;
  transition-duration: ${(p) => p.$transitionDurationSeconds ?? 0.2}s;
  transition-property: background-color;
  transition-timing-function: ${theme.transitions.eases.fastStart};
  width: ${(p) => p.$size}px;
`;

export const AvatarHalo = ({
  alt,
  fallback,
  shape,
  size,
  src,
  transitionDurationSeconds,
  transitionDelaySeconds,
  ...rest
}: AvatarHaloProps) => {
  const { color, isLoading } = useColorThief({ imgUrl: src });
  const prevSrc = usePreviousDistinct(src);
  const prevColor = usePreviousDistinct(color);

  const backgroundColor = useMemo(() => {
    if (!isLoading && color) {
      const [red, green, blue] = color;
      return `rgb(${red}, ${green}, ${blue})`;
    }

    return fallback.toLowerCase();
  }, [color, fallback, isLoading]);

  const hasSrcChanged = src !== prevSrc && typeof prevSrc !== 'undefined';
  const hasColorChanged =
    color !== prevColor && typeof prevColor !== 'undefined';

  return (
    <HaloBackground
      $backgroundColor={backgroundColor}
      $shape={shape}
      $size={size}
      $transitionDelaySeconds={hasSrcChanged ? transitionDelaySeconds : 0}
      $transitionDurationSeconds={
        hasColorChanged ? transitionDurationSeconds : 0
      }
      aria-label={alt}
      {...rest}
    />
  );
};
